import { useState } from 'react';
import { Box, ButtonBase, Collapse, Tooltip, Typography } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { BLOCK_TYPES, PALETTE_DRAG_TYPE } from './blockCatalog';

// Collapsible section header used for the nested palette tree.
const PaletteSection = ({ label, depth = 0, children }) => {
  const [open, setOpen] = useState(true);
  return (
    <Box>
      <ButtonBase
        onClick={() => setOpen(previous => !previous)}
        sx={{
          width: '100%',
          justifyContent: 'space-between',
          px: 1 + depth * 1.5,
          py: 0.75,
          borderRadius: 1,
          typography: 'subtitle2',
          color: 'text.primary',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        {label}
        {open ? <ExpandLessIcon fontSize='small' /> : <ExpandMoreIcon fontSize='small' />}
      </ButtonBase>
      <Collapse in={open}>{children}</Collapse>
    </Box>
  );
};

// A draggable palette tile; drag it onto the canvas or click to add.
const PaletteTile = ({ def, disabled, disabledReason, onAdd }) => {
  const Icon = def.Icon;
  const tile = (
    <Box
      draggable={!disabled}
      onDragStart={event => {
        event.dataTransfer.setData(PALETTE_DRAG_TYPE, def.type);
        event.dataTransfer.effectAllowed = 'copy';
      }}
      onClick={() => {
        if (!disabled) onAdd(def.type);
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 1,
        mb: 1,
        ml: 4,
        mr: 1,
        borderRadius: 1.5,
        border: theme => `1px solid ${theme.palette.divider}`,
        borderLeft: `5px solid ${def.color}`,
        bgcolor: 'background.paper',
        boxShadow: 1,
        cursor: disabled ? 'not-allowed' : 'grab',
        opacity: disabled ? 0.45 : 1,
        '&:hover': disabled ? {} : { boxShadow: 3 },
      }}
    >
      <Icon fontSize='small' sx={{ color: def.color, flexShrink: 0 }} />
      <Typography variant='body2' sx={{ lineHeight: 1.3 }}>
        {def.paletteLabel}
      </Typography>
    </Box>
  );
  if (disabled && disabledReason) {
    return <Tooltip title={disabledReason}>{tile}</Tooltip>;
  }
  return tile;
};

const TRIGGER_TILE_TYPES = [
  'listSignup',
  'tagAdded',
  'occasionDue',
  'consentGranted',
  'bookingCompleted',
  'visitCompleted',
  'bookingNoShow',
  'abandonedBooking',
  'inactivity',
  'manualSegment',
  'apiTrigger',
];

const ACTION_TILE_TYPES = [
  'wait',
  'issueVoucher',
  'increaseVoucherValue',
  'getresponseSync',
  'getresponseTag',
  'addToList',
  'removeFromList',
  'addTag',
  'removeTag',
  'notifyTeam',
];

// "Elemente" sidebar tab: nested tree of draggable block tiles.
const BlockPalette = ({ hasStartBlock, readOnly, onAdd }) => (
  <Box sx={{ py: 1 }}>
    <Typography variant='overline' sx={{ px: 1.5, color: 'text.secondary' }}>
      Automation
    </Typography>
    <PaletteSection label='Trigger (Start)'>
      {TRIGGER_TILE_TYPES.map(type => (
        <PaletteTile
          key={type}
          def={BLOCK_TYPES[type]}
          disabled={readOnly || hasStartBlock}
          disabledReason={
            readOnly
              ? 'Keine Berechtigung zum Bearbeiten'
              : hasStartBlock
                ? 'Es darf nur einen Trigger-Block geben'
                : ''
          }
          onAdd={onAdd}
        />
      ))}
    </PaletteSection>
    <PaletteSection label='Bedingungen'>
      <PaletteTile
        def={BLOCK_TYPES.specialOccasion}
        disabled={readOnly}
        disabledReason={readOnly ? 'Keine Berechtigung zum Bearbeiten' : ''}
        onAdd={onAdd}
      />
    </PaletteSection>
    <PaletteSection label='Aktionen'>
      {ACTION_TILE_TYPES.map(type => (
        <PaletteTile
          key={type}
          def={BLOCK_TYPES[type]}
          disabled={readOnly}
          disabledReason={readOnly ? 'Keine Berechtigung zum Bearbeiten' : ''}
          onAdd={onAdd}
        />
      ))}
    </PaletteSection>
    <PaletteSection label='Filter'>
      <PaletteTile
        def={BLOCK_TYPES.condition}
        disabled={readOnly}
        disabledReason={readOnly ? 'Keine Berechtigung zum Bearbeiten' : ''}
        onAdd={onAdd}
      />
      <PaletteTile
        def={BLOCK_TYPES.conditionalSplit}
        disabled={readOnly}
        disabledReason={readOnly ? 'Keine Berechtigung zum Bearbeiten' : ''}
        onAdd={onAdd}
      />
      <PaletteTile
        def={BLOCK_TYPES.abSplit}
        disabled={readOnly}
        disabledReason={readOnly ? 'Keine Berechtigung zum Bearbeiten' : ''}
        onAdd={onAdd}
      />
    </PaletteSection>
  </Box>
);

export default BlockPalette;
